import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useLocation, useOutletContext, useParams } from "react-router-dom";
import { axiosPrivate } from "../../../api/axios";
import * as XLSX from 'xlsx-js-style';
import { Box, Button, Card, CardHeader, CardContent, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';

import CommonAgGrid from '../../common/DataGrid/CommonAgGrid';
import useGridTransaction from '../../../hooks/useGridTransaction';
import DatagridButtonGroup from '../ButtonGroup/DatagridButtonGroup';
import MachineStatusDialog from './MachineStatusDialog';

const MachineStatusPage = () => {
  const location = useLocation();
  const gridRef = useRef();
  const theme = useTheme();
  const [projectId, setProjectId] = useState(null);
  const { group_code, project_code } = useParams();
  const { groupName, projectName } = useOutletContext();
  const [columnDefs, setColumnDefs] = useState([]);
  const [pageSize, setPageSize] = useState(100);

  // 데이터 다이얼로그 상태 추가
  const [dataDialogOpen, setDataDialogOpen] = useState(false);
  const [dataDialogMode, setDataDialogMode] = useState('add'); // 'add' 또는 'edit'
  const [currentData, setCurrentData] = useState(null);

  // URL 파라미터 처리
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const pid = queryParams.get("pid");
    if (pid) {
      setProjectId(pid);
    }
  }, [location.search]);
  
  // 말풍선 숨김 여부 표시 함수
  const getMonTextHideDisplay = (value) => {
    switch (String(value)) {
      case '0': return '0 : 말풍선 전부 표시';
      case '1': return '1 : 말풍선 비표시';
      case '2': return '2 : 말풍선 클릭 표시';
      case '3': return '3 : Title 표시 / Text 클릭 표시';
      default: return value;
    }
  };
  
  // 데이터 조회 함수
  const fetchMachineStatus = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await axiosPrivate.post('/table/machinestatus', {
        project_id: projectId,
        group_code,
        project_code,
      });

      // 열 설정
      const columnConfig = {
        mchId: { width: 110, headerName: "장비 ID", editable: false },
        mesMchId: { width: 140, headerName: "MES ID", editable: false },
        statusId: { width: 140, headerName: "상태 ID", editable: false },
        lampId: { width: 120, headerName: "램프 ID", editable: false },
        boardType: { width: 150, headerName: "말풍선 타입", editable: false },
        monitorTitle: {
          width: 180,
          headerName: "말풍선 제목",
          cellStyle: { textAlign: 'left' },
          editable: false
        },
        monitorText: { 
          width: 200, 
          headerName: '말풍선 내용', 
          cellStyle: { textAlign: 'left' },
          editable: false
        },
        monTextSize: { width: 170, headerName: "말풍선 텍스트 크기", editable: false },
        monTextHide: { 
          width: 250, 
          headerName: "말풍선 숨김 여부", 
          editable: false,
          cellRenderer: (params) => getMonTextHideDisplay(params.value)
        },
        monitorData: { width: 180, headerName: "식별값", editable: false },
        monitorProc: { width: 170, headerName: "동작 코드", editable: false },
        procStartTime: { width: 190, headerName: "동작 시작 시간", editable: false },
        bufferIn: { width: 150, headerName: "버퍼(인)", editable: false },
        bufferOut: { width: 150, headerName: "버퍼(아웃)", editable: false },
        updateDate: { width: 200, headerName: "업데이트", editable: false }
      };
  
      const fetchedColumns = res.data.columns.map((col) => {
        const baseConfig = {
          field: col.COLUMN_NAME,
          headerName: col.COLUMN_NAME,
          editable: false, // 기본적으로 편집 불가
          type:
            col.DATA_TYPE === 'int' || col.COLUMN_NAME === 'mchId'
              ? 'numericColumn'
              : undefined,
          width: 100,
          headerClass: 'ag-header-center',
          cellStyle: { textAlign: 'center' },
        };
        
        const customConfig = columnConfig[col.COLUMN_NAME] ?? {};
        return { ...baseConfig, ...customConfig };
      });
  
      const dataWithId = res.data.data.map((row, index) => ({
        ...row,
        id: row.id ?? row.mchId ?? index + 1,
        // updateDate가 없으면 현재 시간 추가
        updateDate: row.updateDate ?? new Date().toISOString()
      }));
  
      setColumnDefs(fetchedColumns);
      setRowData(dataWithId);
      setRowTransaction({ add: [], update: [], remove: [] });
    } catch (err) {
      console.error("데이터 조회 실패:", err);
    }
  }, [projectId, group_code, project_code]);
  
  // 트랜잭션 훅 사용
  const {
    rowData,
    setRowData,
    selectedRows,
    setSelectedRows,
    rowTransaction,
    setRowTransaction,
    handleAddRow: baseHandleAddRow,
    handleDeleteRow: baseHandleDeleteRow,
    handleSaveRow: baseHandleSaveRow,
    onCellValueChanged: baseOnCellValueChanged
  } = useGridTransaction(fetchMachineStatus);

  // 데이터 로드
  useEffect(() => {
    if (projectId) {
      fetchMachineStatus();
    }
  }, [projectId, fetchMachineStatus]);

  // 행 추가 핸들러 (다이얼로그 열기)
  const handleAddRow = () => {
    setCurrentData(null);
    setDataDialogMode('add');
    setDataDialogOpen(true);
  };

  // 행 삭제 핸들러
  const handleDeleteRow = async () => {
    if (selectedRows.length === 0) {
      alert("삭제할 항목을 선택하세요.");
      return;
    }

    if (!window.confirm(`선택한 ${selectedRows.length}개 항목을 삭제하시겠습니까?`)) {
      return;
    }

    try {
      const mchIds = selectedRows.map(row => row.mchId);
      
      await axiosPrivate.post('/table/machinestatus/delete', {
        project_id: projectId,
        group_code,
        project_code,
        mchIds
      });

      // 데이터 다시 로드
      await fetchMachineStatus();
      
      alert("삭제되었습니다.");
    } catch (err) {
      console.error("삭제 실패:", err);
      alert("삭제 중 오류가 발생했습니다.");
    }
  };

  // 엑셀 업로드 핸들러
  const handleExcelUpload = (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          alert("업로드할 데이터가 없습니다.");
          return;
        }

        // 데이터베이스의 모든 데이터를 교체하는 것이므로 사용자에게 확인을 받습니다.
        if (window.confirm(`엑셀 파일의 ${jsonData.length}개 데이터로 모든 장비 상태 정보를 교체하시겠습니까? 이 작업은 되돌릴 수 없습니다.`)) {
          uploadData(jsonData);
        }

      } catch (error) {
        console.error("엑셀 파일 처리 중 오류:", error);
        alert("엑셀 파일을 처리하는 중 오류가 발생했습니다.");
      }
    };
    reader.readAsArrayBuffer(file);
    
    // 같은 파일을 다시 선택할 수 있도록 입력 값 초기화
    event.target.value = null;
  };

  // 업로드 데이터 서버 전송
  const uploadData = async (rows) => {
    try {
      await axiosPrivate.post('/table/machinestatus/upload', {
        project_id: projectId,
        group_code,
        project_code,
        rows
      });
      alert("데이터가 성공적으로 업로드되었습니다.");
      fetchMachineStatus(); // 데이터 다시 로드
    } catch (err) {
      console.error("데이터 업로드 실패:", err);
      alert(`데이터 업로드 중 오류가 발생했습니다: ${err.response?.data?.message || err.message}`);
    }
  };

  // 행 저장 핸들러 (기존 트랜잭션 방식 유지)
  const handleSaveRow = async () => {
    await baseHandleSaveRow(
      axiosPrivate,
      "/table/save/machinestatus",
      { project_id: projectId, group_code, project_code }
    );
  };

  // 셀 값 변경 핸들러
  const onCellValueChanged = (params) => {
    // mchId 필드에 대한 유효성 검사
    const validateCell = (params) => {
      if (params.colDef.field === 'mchId') {
        const isDuplicate = rowData.some(row => 
          row.id !== params.data.id && row.mchId === `${params.newValue}`
        );
        
        if (isDuplicate) {
          alert("중복된 mchId입니다.");
          params.node.setDataValue(params.colDef.field, params.oldValue);
          return false;
        }
      }
      return true;
    };

    baseOnCellValueChanged(params, validateCell);
  };

  // 행 선택 변경 핸들러
  const onSelectionChanged = (event) => {
    const selectedNodes = event.api.getSelectedNodes();
    const selectedData = selectedNodes.map(node => node.data);
    setSelectedRows(selectedData);
  };

  // 행 더블클릭 핸들러 (편집 다이얼로그 열기)
  const handleRowDoubleClicked = (event) => {
    setCurrentData(event.data);
    setDataDialogMode('edit');
    setDataDialogOpen(true);
  };

  // 데이터 저장 핸들러
  const handleSaveData = async (data, mode) => {
    try {
      let endpoint = '';
      
      if (mode === 'add') {
        endpoint = '/table/machinestatus/add';
      } else if (mode === 'edit') {
        endpoint = '/table/machinestatus/update';
        // mchId가 있는지 확인 (안전을 위해)
        if (!data.mchId) {
          throw new Error('장비 ID가 없습니다.');
        }
      }
      
      await axiosPrivate.post(endpoint, {
        project_id: projectId,
        group_code,
        project_code,
        data
      });
      
      // 데이터 다시 로드
      await fetchMachineStatus();
      
      // 다이얼로그 닫기
      setDataDialogOpen(false);
      
      // 성공 메시지
      alert(mode === 'add' ? '추가되었습니다.' : '수정되었습니다.');
      
      // 그리드 새로고침 및 컬럼 크기 자동 조정
      setTimeout(() => {
        if (gridRef.current && gridRef.current.api) {
          gridRef.current.api.sizeColumnsToFit();
          gridRef.current.api.autoSizeAllColumns();
        }
      }, 100);
    } catch (err) {
      console.error("데이터 저장 실패:", err);
      alert("데이터 저장 중 오류가 발생했습니다.");
    }
  };

  // 다이얼로그 닫기 핸들러
  const handleCloseDialog = () => {
    setDataDialogOpen(false);
  };

  // 엑셀 내보내기
  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(rowData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "MachineStatus");
    XLSX.writeFile(wb, `MachineStatus_${project_code}.xlsx`);
  };

  return (
    <Grid container spacing={3} sx={{ height: "100%" }}>
      <Grid size={{ xs: 12 }} sx={{ height: "100%"}}>
        <Card sx={{
          height: "100%",
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '0',
          bgcolor: theme.palette.mode === 'dark' 
            ? 'var(--mui-palette-background-default)' 
            : 'var(--mui-palette-background-paper)'
        }}>
          <CardHeader
            title={`장비 데이터 테이블`}
            subheader={`프로젝트: ${projectName}`}
            action={
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  color="success"
                  component="label"
                  startIcon={<FileUploadIcon />}
                >
                  Excel 업로드
                  <input
                    type="file"
                    hidden
                    accept=".xlsx, .xls"
                    onChange={handleExcelUpload}
                  />
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportExcel}
                >
                  Excel 다운로드
                </Button>
              </Box>
            }
          />
          
          <DatagridButtonGroup
            handleAddRow={handleAddRow}
            handleDeleteRow={handleDeleteRow}
            handleSaveRow={handleSaveRow}
            selectionModel={selectedRows.length > 0}
          />

          <CardContent sx={{ 
            flexGrow: 1, 
            display: 'flex', 
            flexDirection: 'column',
            paddingInline: 6,
            paddingBlock: 2,
            height: 0
          }}>
            <CommonAgGrid
              gridRef={gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              onSelectionChanged={onSelectionChanged}
              onCellValueChanged={onCellValueChanged}
              onRowDoubleClicked={handleRowDoubleClicked}
              pageSize={pageSize}
            />
          </CardContent>
        </Card>
      </Grid>
      
      {/* 데이터 다이얼로그 */}
      <MachineStatusDialog
        open={dataDialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSaveData}
        data={currentData}
        mode={dataDialogMode}
        projectId={projectId}
        group_code={group_code}
        project_code={project_code}
        existingData={rowData} // 기존 데이터 전달
      />
    </Grid>
  );
};

export default MachineStatusPage;